import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const chunk4 = (ids) => {
  const per = Math.ceil(ids.length / 4) || 1;
  return [0, 1, 2, 3].map((i) => ids.slice(i * per, (i + 1) * per));
};

// Saved layout wins; tabs added since saving land in the shortest column, removed ones vanish.
const merge = (ids, saved) => {
  if (!saved || !Array.isArray(saved)) return chunk4(ids);
  const cols = [[], [], [], []];
  const seen = new Set();
  saved.slice(0, 4).forEach((col, i) => (col || []).forEach((id) => {
    if (ids.includes(id) && !seen.has(id)) { cols[i].push(id); seen.add(id); }
  }));
  ids.forEach((id) => {
    if (!seen.has(id)) cols.reduce((a, b) => (a.length <= b.length ? a : b)).push(id);
  });
  return cols;
};

export const TabBoard = ({ tabs, tab, selectTab }) => {
  const [cols, setCols] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const dragId = useRef(null);
  const labelOf = Object.fromEntries(tabs.map((t) => [t.id, t.label]));
  const ids = tabs.map((t) => t.id);
  const key = ids.join(",");

  useEffect(() => {
    api.get("/me/admin-tabs")
      .then(({ data }) => setCols(merge(ids, data.columns)))
      .catch(() => setCols(chunk4(ids)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!cols) return <div className="mt-6 mb-6 h-10" />;

  const persist = (next) => {
    setCols(next);
    api.put("/me/admin-tabs", { columns: next }).catch(() => {});
  };

  const dropAt = (colIdx, beforeId = null) => {
    const id = dragId.current;
    dragId.current = null;
    setOverCol(null);
    if (!id) return;
    const next = cols.map((c) => c.filter((x) => x !== id));
    const target = next[colIdx];
    const at = beforeId && beforeId !== id ? target.indexOf(beforeId) : -1;
    if (at >= 0) target.splice(at, 0, id); else target.push(id);
    persist(next);
  };

  const reset = () => {
    api.put("/me/admin-tabs", { columns: null }).catch(() => {});
    setCols(chunk4(ids));
  };

  return (
    <div className="mt-6 mb-6" data-testid="tab-board">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cols.map((col, ci) => (
          <div key={ci}
            className="flex flex-col gap-1.5 min-h-[42px] rounded-xl p-1.5 transition-colors"
            style={{ background: overCol === ci ? "rgba(204,255,0,0.18)" : "transparent",
                     border: overCol === ci ? "1px dashed #A3C900" : "1px dashed transparent" }}
            data-testid={`tab-col-${ci}`}
            onDragOver={(e) => { e.preventDefault(); setOverCol(ci); }}
            onDragLeave={() => setOverCol((c) => (c === ci ? null : c))}
            onDrop={(e) => { e.preventDefault(); dropAt(ci); }}>
            {col.map((id) => (
              <button key={id} draggable data-testid={`admin-tab-${id}`}
                onDragStart={(e) => { dragId.current = id; e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); dropAt(ci, id); }}
                onClick={() => selectTab(id)}
                className="px-2 sm:px-3 py-1.5 rounded-full text-xs font-display font-semibold text-center leading-tight transition-all"
                style={{
                  background: tab === id ? "#CCFF00" : "#FFFFFF",
                  color: tab === id ? "#0A0D14" : "#52525B",
                  border: "1px solid " + (tab === id ? "#CCFF00" : "#C4C8CE"),
                  cursor: "grab",
                }}>
                {labelOf[id]}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="text-right mt-1">
        <span className="text-[10px] text-[#000000]">Drag tabs between columns to re-arrange · </span>
        <button className="text-[10px] text-[#000000] underline bg-transparent border-0 cursor-pointer p-0"
          onClick={reset} data-testid="tab-layout-reset">Reset layout</button>
      </div>
    </div>
  );
};
